import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2, PhoneCall } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useVehicles, vehicleName } from "@/lib/useVehicles";
import { usePublicPricing, packageBase } from "@/lib/usePublicPricing";
import { LivePricingHint } from "@/components/LivePricingHint";
import { SlotPicker } from "@/components/SlotPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", email: "", phone: "", vehicle: "", coverage_selection: "own", damage_waiver: false,
  pickup_date: "", return_date: "", pickup_time: "", return_time: "",
  address: "", date_of_birth: "", total_override: "",
};

const inputCls = "mt-1 w-full bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50 [color-scheme:dark]";

export default function ManualBooking({ token, onCreated }) {
  const { vehicles } = useVehicles();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [policyAck, setPolicyAck] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const car = useMemo(() => vehicles.find((c) => vehicleName(c) === form.vehicle), [vehicles, form.vehicle]);
  const days = useMemo(() => {
    if (!form.pickup_date || !form.return_date) return 0;
    const d = Math.round((new Date(`${form.return_date}T00:00`) - new Date(`${form.pickup_date}T00:00`)) / 86400000);
    return d > 0 ? d : 0;
  }, [form.pickup_date, form.return_date]);
  const insuranceType = form.coverage_selection === "rental" ? "AllIn" : "BYOI";
  const waiverOn = !!form.damage_waiver;
  const pubPricing = usePublicPricing();
  const dailyRate = pubPricing.additional_day_rate;
  const baseTotal = days > 0 ? packageBase(pubPricing, days) : 0;
  const computedTotal = baseTotal
    + (insuranceType === "AllIn" ? pubPricing.allin_coverage_day * days : 0)
    + (waiverOn ? pubPricing.damage_waiver_day * days : 0);
  const total = form.total_override !== "" ? parseFloat(form.total_override) || 0 : computedTotal;

  const submit = async () => {
    if (!form.name || !form.email || !form.phone || !form.vehicle) return toast.error("Name, email, phone and vehicle are required.");
    if (!form.pickup_date || !form.return_date) return toast.error("Choose pickup and return dates.");
    if (!form.pickup_time || !form.return_time) return toast.error("Choose a pickup and return appointment time.");
    if (days < 3) return toast.error("Minimum rental is 3 calendar days.");
    if (!policyAck) return toast.error("You must acknowledge the 24-Hour Nonpayment Return Policy.");
    setBusy(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name, email: form.email, phone: form.phone, vehicle: form.vehicle,
        insurance_type: insuranceType, coverage_selection: form.coverage_selection, coverage_ack: true,
        add_damage_protection: waiverOn, pickup_date: form.pickup_date, return_date: form.return_date,
        pickup_time: form.pickup_time, return_time: form.return_time, address: form.address,
        date_of_birth: form.date_of_birth, days, daily_rate: dailyRate, total_price: total,
        message: "Created manually by admin (phone booking).",
        lead_source: "Manual Entry",
        nonpayment_policy_ack: true,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Booking created for ${form.name}.`);
      setForm(empty); setPolicyAck(false); setOpen(false); onCreated?.();
    } catch (e) {
      const s = e.response?.status;
      if (s === 409 || s === 400) toast.error(e.response.data?.detail || "Could not create booking.");
      else toast.error("Something went wrong creating the booking.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button data-testid="new-booking-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-sm bg-[#F2B705] text-black font-bold px-4 py-2 rounded-md hover:bg-white transition-colors">
        <Plus size={16} /> New Booking
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="manual-booking-dialog" className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><PhoneCall size={18} className="text-[#F2B705]" /> New Booking (Phone / Walk-in)</DialogTitle>
            <DialogDescription className="text-white/45">Book a customer who called in. Availability and the 3-day minimum are enforced.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-white/45">Name</label><input data-testid="mb-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div><label className="text-xs uppercase tracking-widest text-white/45">Phone</label><input data-testid="mb-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            </div>
            <div><label className="text-xs uppercase tracking-widest text-white/45">Email</label><input data-testid="mb-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="for confirmation & agreement" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Vehicle</label>
                <select data-testid="mb-vehicle" className={inputCls} value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)}>
                  <option value="">Select vehicle</option>
                  {vehicles.filter((c) => c.status === "available").map((c) => <option key={c.id} value={vehicleName(c)}>{c.year} {vehicleName(c)} — ${c.byoi_day}/day</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Rental Coverage</label>
                <select data-testid="mb-coverage" className={inputCls} value={form.coverage_selection} onChange={(e) => set("coverage_selection", e.target.value)}>
                  <option value="own">Option 1 — Own Insurance (${car?.byoi_day || "—"}/day)</option>
                  <option value="rental">Option 2 — Third-Party Rental Insurance</option>
                  <option value="decline">Option 3 — Decline Optional Insurance</option>
                </select>
                <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
                  <input type="checkbox" data-testid="mb-waiver" checked={!!form.damage_waiver} onChange={(e) => set("damage_waiver", e.target.checked)} className="h-3.5 w-3.5 accent-[#F2B705]" />
                  <span className="text-[11px] text-white/60">Optional Damage Waiver — $15/day (not insurance)</span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-white/45">Pickup Date</label><input data-testid="mb-pickup" type="date" className={inputCls} value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} /></div>
              <div><label className="text-xs uppercase tracking-widest text-white/45">Return Date</label><input data-testid="mb-return" type="date" className={inputCls} value={form.return_date} onChange={(e) => set("return_date", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-white/45">Pickup Appointment Time</label><SlotPicker testid="mb-pickup-time" date={form.pickup_date} value={form.pickup_time} onChange={(v) => set("pickup_time", v)} className={inputCls} /></div>
              <div><label className="text-xs uppercase tracking-widest text-white/45">Return Appointment Time</label><SlotPicker testid="mb-return-time" date={form.return_date} value={form.return_time} onChange={(v) => set("return_time", v)} className={inputCls} /></div>
            </div>
            <div><label className="text-xs uppercase tracking-widest text-white/45">Address <span className="text-white/30 normal-case">(optional)</span></label><input data-testid="mb-address" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-white/45">Date of Birth <span className="text-white/30 normal-case">(optional)</span></label><input data-testid="mb-dob" type="date" className={inputCls} value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></div>
              <div><label className="text-xs uppercase tracking-widest text-white/45">Total $ <span className="text-white/30 normal-case">(auto)</span></label><input data-testid="mb-total" type="number" step="0.01" className={inputCls} value={form.total_override} onChange={(e) => set("total_override", e.target.value)} placeholder={computedTotal ? `$${computedTotal}` : "auto"} /></div>
            </div>

            <LivePricingHint pickup={form.pickup_date} ret={form.return_date} />
            <div className="flex items-center justify-between bg-[#0C0C0C] border border-white/10 rounded-lg px-4 py-2.5 text-sm">
              <span className="text-white/55">{days || 0} day{days !== 1 ? "s" : ""} × ${dailyRate || 0}/day</span>
              <span className="font-display font-black text-lg text-gold-gradient">Total ${total || 0}</span>
            </div>

            <label className="flex items-start gap-2 text-xs text-white/70 bg-red-500/[0.06] border border-red-500/30 rounded-lg px-3 py-2.5">
              <input data-testid="mb-nonpayment-ack" type="checkbox" checked={policyAck} onChange={(e) => setPolicyAck(e.target.checked)} className="mt-0.5 accent-[#F2B705]" />
              <span><b className="text-red-300">24-Hour Nonpayment Return Policy Acknowledged</b> — if the rental becomes overdue and no payment or approved arrangement is made within 24 hours, mandatory vehicle return/recovery begins automatically. Failure to return the vehicle results in Do Not Rent placement.</span>
            </label>

            <button data-testid="mb-submit" onClick={submit} disabled={busy}
              className="w-full bg-[#F2B705] text-black font-bold py-3 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Create Booking
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
